import React, { useState, useEffect } from 'react';
import { fbFunctions } from '../../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isButtonActive, setIsButtonActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Animation effect on component mount
  useEffect(() => {
    setIsVisible(true);
  }, []);


const handleSubmit = (e) => {
  e.preventDefault();
  
  // Basic email validation
  if (!email || !/\S+@\S+\.\S+/.test(email)) {
    setError('Please enter a valid email address');
    return;
  }
  
  // Set submitting state to show loading indicator if needed
  setIsSubmitting(true);
  setError('');
  
  // Call Firebase function using httpsCallable
  const newsletterSignup = httpsCallable(fbFunctions, 'newsletter_signup');
  
  newsletterSignup({
    email: email,
    source: 'maintenance_page'
  })
    .then((result) => {
      if (result.data && result.data.status === 'success') {
        // Clear the form and show success state
        setEmail('');
        
        // Slight delay before showing success message for better animation effect
        setTimeout(() => {
          setSubscribed(true);
        }, 600);
      } else {
        // Handle other status responses
        throw new Error(result.data?.message || 'Failed to subscribe');
      }
    })
    .catch((error) => {
      console.error('Error subscribing to newsletter:', error);
      setError(error.message || 'An error occurred. Please try again.');
    })
    .finally(() => {
      setIsSubmitting(false);
    });
};

  // Styles with improved layout, white text, and animation
  const styles = {
    container: {
      marginTop: '10px',
      padding: '40px 30px',
      backgroundColor: '#111d4a',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.8s ease-out',
    },
    innerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
    },
    heading: {
      fontSize: '2.25rem',
      fontWeight: '600',
      marginBottom: '24px',
      color: '#ffffff',
      letterSpacing: '-0.025em',
      transition: 'opacity 0.8s ease 0.3s',
      opacity: isVisible ? 1 : 0,
    },
    description: {
      fontSize: '1.125rem',
      lineHeight: '1.7',
      marginBottom: '36px',
      maxWidth: '650px',
      color: '#e2e8f0', // Light gray for better readability on dark background
      transition: 'opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
    },
    form: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '16px',
      maxWidth: '650px',
      alignItems: 'flex-start',
      transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
    },
    inputContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 300px',
    },
    input: {
      padding: '16px 20px',
      fontSize: '1.05rem',
      lineHeight: '1.5',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05) inset',
      transition: 'all 0.4s ease',
    },
    inputFocus: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.1)',
      outline: 'none',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
    },
    // Enhanced secondary button with animation
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px 28px',
      backgroundColor: '#fff',
      color: '#ffffff',
      fontSize: '1rem',
      fontWeight: '600',
      lineHeight: '1.5',
      borderRadius: '2rem',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      position: 'relative',
      zIndex: 1,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      boxShadow: isButtonHovered 
        ? '0 6px 16px rgba(0, 0, 0, 0.2)' 
        : '0 4px 8px rgba(0, 0, 0, 0.1)',
      transform: isButtonHovered 
        ? 'translateY(-2px)' 
        : isButtonActive 
          ? 'translateY(1px)' 
          : 'translateY(0)',
      backgroundColor: isButtonHovered 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'transparent',
      borderColor: isButtonHovered 
        ? '#ffffff' 
        : 'rgba(255, 255, 255, 0.5)',
    },
    errorText: {
      color: '#f87171',
      fontSize: '0.875rem',
      marginTop: '8px',
      marginLeft: '4px',
      transition: 'all 0.4s ease',
    },
    successContainer: {
      marginTop: '20px',
      display: 'flex',
      alignItems: 'center',
      padding: '20px 24px',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      borderRadius: '8px',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      transform: 'scale(0.95)',
      opacity: 0,
      animation: 'successAppear 0.8s forwards',
    },
    successIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: '#10b981',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '16px',
      flexShrink: 0,
    },
    successText: {
      fontSize: '1.05rem',
      margin: 0,
      color: '#ffffff',
      lineHeight: '1.6',
    },
    footerText: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.7)',
      marginTop: '24px',
      maxWidth: '650px',
      transition: 'opacity 0.8s ease 0.6s',
      opacity: isVisible ? 1 : 0,
    },
    link: {
      color: '#ffffff',
      textDecoration: 'none',
      borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
      transition: 'all 0.3s ease',
    },
    linkHover: {
      borderColor: '#ffffff',
      color: '#ffffff',
    },
    // Keyframes for animations
    '@keyframes successAppear': {
      '0%': {
        transform: 'scale(0.95)',
        opacity: 0,
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 1,
      }
    },
    ripple: {
      position: 'absolute',
      borderRadius: '50%',
      transform: 'scale(0)',
      animation: 'ripple 0.8s linear',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
    '@keyframes ripple': {
      to: {
        transform: 'scale(4)',
        opacity: 0,
      }
    }
  };

  // Button ripple effect
  const createRipple = (event) => {
    const button = event.currentTarget;
    const ripple = document.createElement('span');
    
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    
    const rect = button.getBoundingClientRect();
    
    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${event.clientX - rect.left - radius}px`;
    ripple.style.top = `${event.clientY - rect.top - radius}px`;
    
    for (const styleProperty in styles.ripple) {
      ripple.style[styleProperty] = styles.ripple[styleProperty];
    }
    
    button.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
    }, 800);
  };

  return (
    <div style={styles.container}>
      <div style={styles.innerContainer}>
        <h2 style={styles.heading}>
          Updates from PlasticAnalytics
        </h2>
        
        {subscribed ? (
          <div style={{
            ...styles.successContainer,
            animation: 'successAppear 0.8s forwards',
          }}>
            <div style={styles.successIcon}>
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p style={styles.successText}>
              You're subscribed to our newsletter. We'll keep you updated on the latest news and research.
            </p>
          </div>
        ) : (
          <>
            <p style={styles.description}>
              Get the latest on microplastics research, our spectral library updates, and industry news directly to your inbox.
            </p>
            
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputContainer}>
                <input
                  type="email"
                  placeholder="Work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  onFocus={(e) => {
                    Object.assign(e.target.style, styles.inputFocus);
                  }}
                  onBlur={(e) => {
                    // Reset to original styles
                    Object.keys(styles.inputFocus).forEach(key => {
                      e.target.style[key] = styles.input[key] || '';
                    });
                  }}
                />
                {error && (
                  <p style={styles.errorText}>
                    {error}
                  </p>
                )}
              </div>
              <div style={styles.buttonContainer}>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  style={styles.button}
                  onMouseDown={(e) => {
                    setIsButtonActive(true);
                    createRipple(e);
                  }}
                  onMouseUp={() => setIsButtonActive(false)}
                  onMouseEnter={() => setIsButtonHovered(true)}
                  onMouseLeave={() => {
                    setIsButtonHovered(false);
                    setIsButtonActive(false);
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Notify Me'}
                </button>
              </div>
            </form>
            
            <p style={styles.footerText}>
              By subscribing, you agree to our{' '}
              <a 
                href="/privacy-policy" 
                style={styles.link}
                onMouseOver={(e) => {
                  Object.assign(e.currentTarget.style, styles.linkHover);
                }}
                onMouseOut={(e) => {
                  Object.keys(styles.linkHover).forEach(key => {
                    e.currentTarget.style[key] = styles.link[key] || '';
                  });
                }}
              >
                Privacy Policy
              </a>
              {' '}and consent to receive updates from PlasticAnalytics.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterSignup;